/**
 * One list of category suggestions from every source Leo has.
 *
 * Three readings meet here: the keyword reading of the stem (instant,
 * explainable), the model's reading of the whole question (only ever from the
 * catalog it was handed; any other id is dropped), and the director's
 * content-to-standard mapping. Where two sources name the same category the
 * stronger one stands, and agreement raises a model pick. What people have
 * added and skipped before nudges the result. At most three come back, best
 * first, each with a confidence that reads High, Medium or Low.
 */

import { explainConfidence, suggestCategoriesByDimension } from "./suggest"
import { buildDimensionLookup, type CategoryDimension } from "./dimensions"
import type { CrosswalkLink } from "./crosswalk"
import { adjustConfidence, type SuggestionFeedback } from "./suggestion-feedback"
import type { ExamCategory } from "./types"

export type CategoryPick = {
  categoryId: string
  dimensionId: string
  confidence: number
  reason: string
  /** Why this confidence, one line each. */
  why: string[]
}

export function mergeCategoryPicks(input: {
  stem: string
  /** What this surface offers: the only categories that may come back. */
  offered: ExamCategory[]
  /** The trees this list covers. */
  dimensions: readonly CategoryDimension[]
  /** Tags already on the question. */
  value: readonly string[]
  dismissedIds: readonly string[]
  isDirector: boolean
  /** What the model said, if it answered. */
  modelPicks: ReadonlyArray<{ categoryId: string; reason: string; fit?: "high" | "medium" | "low" }>
  /**
   * Where the model is. Once it has answered, the model is the reader: the
   * keyword reading only confirms what it picked and is never offered on its
   * own. Until then (or if it cannot be reached) the keywords stand in.
   */
  modelStatus?: "idle" | "pending" | "answered" | "unavailable"
  links: readonly CrosswalkLink[]
  feedback: SuggestionFeedback
  minConfidence?: number
  limit?: number
}): CategoryPick[] {
  const { stem, offered, value, dismissedIds } = input
  const dimensionIds = new Set(input.dimensions.map((dimension) => dimension.id))
  const lookup = buildDimensionLookup(offered)
  const byId = new Map<string, CategoryPick>()
  const keep = (pick: CategoryPick) => {
    const have = byId.get(pick.categoryId)
    if (!have || pick.confidence > have.confidence) byId.set(pick.categoryId, pick)
  }

  const engine = suggestCategoriesByDimension({
    stem,
    categories: offered,
    dimensions: input.dimensions,
    assignedIds: value,
    dismissedIds,
    isDirector: input.isDirector,
    perDimension: 3,
  })
  const modelAnswered = input.modelStatus === "answered"
  for (const row of modelAnswered ? [] : engine) {
    const category = offered.find((c) => c.id === row.categoryId)
    keep({
      ...row,
      why: category
        ? explainConfidence({
            stem,
            category,
            categories: offered,
            confidence: row.confidence,
            source: row.reason.startsWith("Sits beside") ? "nearby" : "keywords",
          })
        : [],
    })
  }

  for (const pick of input.modelPicks) {
    const category = offered.find((row) => row.id === pick.categoryId)
    // Outside the catalog, already chosen, or turned down: not offered.
    if (!category || value.includes(pick.categoryId) || dismissedIds.includes(pick.categoryId)) continue
    const dimensionId = lookup.get(pick.categoryId) ?? ""
    if (!dimensionIds.has(dimensionId)) continue
    const agreed = engine.some((row) => row.categoryId === pick.categoryId)
    // The model's own judgement of the fit sets the level; agreement from the
    // words lifts it a little.
    const base = pick.fit === "high" ? 0.88 : pick.fit === "low" ? 0.4 : 0.65
    const confidence = Math.min(0.97, agreed ? base + 0.07 : base)
    keep({
      categoryId: pick.categoryId,
      dimensionId,
      confidence,
      reason: pick.reason,
      why: explainConfidence({ stem, category, categories: offered, confidence, source: "model", agreed, fit: pick.fit ?? "medium" }),
    })
  }

  for (const link of input.links) {
    if (!value.includes(link.contentId) || value.includes(link.standardId)) continue
    if (dismissedIds.includes(link.standardId)) continue
    const dimensionId = lookup.get(link.standardId) ?? ""
    if (!dimensionIds.has(dimensionId)) continue
    const from = offered.find((row) => row.id === link.contentId)?.name ?? "the content category"
    const target = offered.find((row) => row.id === link.standardId)
    const confidence = link.status === "confirmed" ? 0.9 : 0.7
    keep({
      categoryId: link.standardId,
      dimensionId,
      confidence,
      reason: `Mapped from ${from}.`,
      why: target
        ? explainConfidence({ stem, category: target, categories: offered, confidence, source: "mapping", mappedFrom: from, mappingConfirmed: link.status === "confirmed" })
        : [],
    })
  }

  return [...byId.values()]
    .filter((pick) => pick.confidence >= (input.minConfidence ?? 0))
    .map((pick) => {
      const adjusted = adjustConfidence(pick.confidence, pick.categoryId, input.feedback)
      const note =
        adjusted < pick.confidence
          ? "People have skipped this suggestion before, so it is shown lower."
          : adjusted > pick.confidence
            ? "People have added this suggestion before, so it is shown a little higher."
            : null
      return { ...pick, confidence: adjusted, why: note ? [...pick.why.slice(0, -1), note, ...pick.why.slice(-1)] : pick.why }
    })
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, input.limit ?? 3)
}
